import { useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Banners } from '@sorare/core/src/components/layout/Banners';
import { MobileAppBottomPortalReceiver } from '@sorare/core/src/components/navigation/AppNavigation';
import {
  sportAnalyticTag,
  useSportContext,
} from '@sorare/core/src/contexts/sport';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import {
  SaleType,
  TOKENS_STACKED_LIMIT,
  createCardSorts,
} from '@sorare/core/src/lib/algolia';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import { PageTemplate } from '@sorare/marketplace/src/components/market/PageTemplate';
import { useDefaultFilters } from '@sorare/marketplace/src/hooks/useDefaultFilters';
import { AdvancedCardSearch } from '@sorare/marketplace/src/searchCards/AdvancedCardSearch';
import { instantBuyMarketFilters as footballFilters } from '@sorare/marketplace/src/searchCards/football/cardFilters';
import { instantBuyMarketFilters as baseballFilters } from '@sorare/marketplace/src/searchCards/mlb/cardFilters';
import { instantBuyMarketFilters as nbaFilters } from '@sorare/marketplace/src/searchCards/nba/cardFilters';

const instantBuyMarketFilters = {
  [Sport.FOOTBALL]: () => footballFilters,
  [Sport.NBA]: () => nbaFilters,
  [Sport.BASEBALL]: baseballFilters,
};

const InstantBuyPage = () => {
  const { sport } = useSportContext();
  const { isIosApp, isMarketAuthorizedIosApp } = useIsMobileApp();

  const {
    flags: { useMlbSeasonAverageScore = true },
  } = useFeatureFlags();

  const [searchParams] = useSearchParams();
  const isOnboardingFlow = !!searchParams.get('isOnboardingFlow');
  const isNewNavigation = !!searchParams.get('isNewNavigation');

  useTitleAndDescription(
    metadatas.instantBuy.title,
    metadatas.instantBuy.description
  );

  const filters = useDefaultFilters({ primaryOffer: true, bundled: false });

  const defaultSort = 'Cards On Sale Popular Player';

  const cardSorts = useMemo(
    () =>
      createCardSorts({
        indexPrefix: 'Cards On Sale',
        withBestValue: false,
        withHighestAverageScore: true,
        withEndingSoon: false,
        withNew: false,
        withPopularPlayer: true,
      }),
    []
  );

  const cardFilters = useMemo(
    () => instantBuyMarketFilters[sport]?.(useMlbSeasonAverageScore),
    [sport, useMlbSeasonAverageScore]
  );

  return (
    <>
      {!!(!isIosApp || isMarketAuthorizedIosApp) && <Banners />}
      <PageTemplate showBack>
        <AdvancedCardSearch
          sport={sport}
          sorts={cardSorts}
          defaultSort={defaultSort}
          cardFilters={cardFilters}
          defaultFilters={filters}
          analyticsTags={['InstantBuy', sportAnalyticTag[sport]]}
          topic={{ type: 'instantBuy', label: '' }}
          onlyPrimary
          stackableTypes={[SaleType.PRIMARY_OFFER]}
          hideStackedSwitch
          distinct={TOKENS_STACKED_LIMIT}
          hideSorareUser
          removeFinishedPrimaryBuys
          displayVideoTutorial={sport === Sport.FOOTBALL}
          isOnboardingFlow={isOnboardingFlow}
          isNewNavigation={isNewNavigation}
        />
      </PageTemplate>

      {!!isIosApp && (
        <MobileAppBottomPortalReceiver id="above-bottom-bar-portal" />
      )}
    </>
  );
};
export default InstantBuyPage;
